// utils/meetingDataUtils.js - Utilities for fetching external meeting data

import { axiosInstance } from "../../../../utils/axios";

/**
 * Fetch meeting recording URL
 * @param {string} externalMeetingId - The external meeting ID
 * @returns {Promise<string>} - The recording download URL
 */
export const fetchMeetingRecording = async (externalMeetingId) => {
  if (!externalMeetingId) {
    throw new Error("External meeting ID is required");
  }

  // Return a URL that goes through our backend proxy
  return `/api/meetings/${externalMeetingId}/download_recording/`;
};

/**
 * Fetch meeting transcript URL
 * @param {string} externalMeetingId - The external meeting ID
 * @returns {Promise<string>} - The transcript download URL
 */
export const fetchMeetingTranscript = async (externalMeetingId) => {
  if (!externalMeetingId) {
    throw new Error("External meeting ID is required");
  }

  // Return a URL that goes through our backend proxy
  return `/api/meetings/${externalMeetingId}/download_transcript/`;
};

/**
 * Fetch meeting summary data
 * @param {string} externalMeetingId - The external meeting ID
 * @returns {Promise<object>} - The meeting summary data
 */
export const fetchMeetingSummary = async (externalMeetingId) => {
  if (!externalMeetingId) {
    throw new Error("External meeting ID is required");
  }

  try {
    // Use GraphQL to fetch the meeting summary through our backend
    const response = await axiosInstance.post("/graphql", {
      query: `
        query GetMeetingSummary($externalMeetingId: String!) {
          getMeetingSummary(externalMeetingId: $externalMeetingId)
        }
      `,
      variables: { externalMeetingId },
    });

    if (response.data?.data?.getMeetingSummary) {
      // The response is already in the correct format, just return it
      return response.data.data.getMeetingSummary;
    } else if (response.data?.getMeetingSummary) {
      // Handle case where data might be directly in response
      return response.data.getMeetingSummary;
    }

    throw new Error("No summary data received");
  } catch (error) {
    console.error("Error fetching meeting summary:", error);
    throw error;
  }
};

/**
 * Download a file from URL
 * @param {string} url - The file URL
 * @param {string} filename - The filename for download
 */
export const downloadFile = (url, filename) => {
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.target = "_blank";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Format meeting summary data for display
 * @param {object} summaryData - Raw summary data from API
 * @returns {object} - Formatted summary data
 */
export const formatMeetingSummary = (summaryData) => {
  if (!summaryData) return null;

  // Handle different possible formats of summary data
  if (typeof summaryData === "string") {
    try {
      return JSON.parse(summaryData);
    } catch (e) {
      return { summary: summaryData };
    }
  }

  return summaryData;
};
